import { useState, useRef } from "react";
import { Upload, Loader2, UserPlus, UserMinus, RefreshCw, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { api, formatApiError } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function ImportDialog({ open, onOpenChange, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [recap, setRecap] = useState(null);
  const [deleteAbsent, setDeleteAbsent] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setRecap(null);
    setDeleteAbsent(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const analyse = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/import/excel/preview", form);
      setRecap(data);
    } catch (err) {
      toast.error(formatApiError(err, "Fichier illisible"));
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/import/excel/apply", { draft_id: recap.draft_id, supprimer_absents: deleteAbsent });
      toast.success(`Import terminé : ${data.crees} créé(s), ${data.mis_a_jour} mis à jour, ${data.supprimes} supprimé(s)`);
      onOpenChange(false);
      reset();
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-w-2xl" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-emerald-500" /> Importer la liste du personnel (Excel)</DialogTitle>
          <DialogDescription>Même format que votre fichier initial. Le récapitulatif des changements s'affiche avant application — rien n'est modifié sans votre validation.</DialogDescription>
        </DialogHeader>

        {!recap && (
          <div className="py-6 text-center space-y-4">
            <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={analyse} data-testid="import-file-input" />
            <Button onClick={() => fileRef.current?.click()} disabled={loading} variant="outline" data-testid="import-choose-file">
              {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyse du fichier...</> : <><Upload className="h-4 w-4 mr-2" /> Choisir le fichier .xlsx</>}
            </Button>
            <p className="text-xs text-muted-foreground">Le fichier est analysé puis comparé à la liste actuelle du site.</p>
          </div>
        )}

        {recap && (
          <div className="space-y-4" data-testid="import-recap">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-lg border border-emerald-700 bg-emerald-950/60 p-3 text-center" data-testid="import-count-nouveaux">
                <p className="text-xl font-extrabold font-mono text-emerald-400">{recap.nouveaux.length}</p>
                <p className="text-[10px] text-muted-foreground uppercase">Nouveaux</p>
              </div>
              <div className="rounded-lg border border-amber-700 bg-amber-950/60 p-3 text-center" data-testid="import-count-modifies">
                <p className="text-xl font-extrabold font-mono text-amber-400">{recap.modifies.length}</p>
                <p className="text-[10px] text-muted-foreground uppercase">Modifiés</p>
              </div>
              <div className="rounded-lg border border-red-700 bg-red-950/60 p-3 text-center" data-testid="import-count-absents">
                <p className="text-xl font-extrabold font-mono text-red-400">{recap.absents.length}</p>
                <p className="text-[10px] text-muted-foreground uppercase">Absents du fichier</p>
              </div>
              <div className="rounded-lg border border-border bg-secondary/40 p-3 text-center" data-testid="import-count-inchanges">
                <p className="text-xl font-extrabold font-mono">{recap.inchanges}</p>
                <p className="text-[10px] text-muted-foreground uppercase">Inchangés</p>
              </div>
            </div>

            <div className="max-h-64 overflow-y-auto space-y-3 pr-1">
              {recap.nouveaux.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-emerald-400 flex items-center gap-1.5 mb-1.5"><UserPlus className="h-3.5 w-3.5" /> Seront ajoutés</p>
                  {recap.nouveaux.map((p, i) => (
                    <p key={`${p.nom}|${p.prenom}`} className="text-sm px-2 py-1" data-testid={`import-nouveau-${i}`}>{p.prenom} {p.nom} <span className="text-muted-foreground">— {p.service}</span></p>
                  ))}
                </div>
              )}
              {recap.modifies.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-400 flex items-center gap-1.5 mb-1.5"><RefreshCw className="h-3.5 w-3.5" /> Seront mis à jour</p>
                  {recap.modifies.map((p, i) => (
                    <div key={p.id} className="text-sm px-2 py-1.5 border-b border-border/50" data-testid={`import-modifie-${i}`}>
                      <span className="font-medium">{p.prenom} {p.nom}</span>
                      {p.changements.map((c) => (
                        <p key={c.champ} className="text-xs text-muted-foreground ml-3">{c.champ} : <span className="line-through text-red-400/80">{c.avant}</span> → <span className="text-emerald-400">{c.apres}</span></p>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {recap.absents.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-red-400 flex items-center gap-1.5 mb-1.5"><UserMinus className="h-3.5 w-3.5" /> Absents du fichier (sur le site uniquement)</p>
                  {recap.absents.map((p, i) => (
                    <p key={p.id} className="text-sm px-2 py-1" data-testid={`import-absent-${i}`}>{p.prenom} {p.nom} <span className="text-muted-foreground">— {p.service}</span></p>
                  ))}
                  <label className="flex items-center gap-2.5 mt-2 px-2 py-2 rounded-lg border border-red-800/60 bg-red-950/30 cursor-pointer">
                    <Checkbox checked={deleteAbsent} onCheckedChange={setDeleteAbsent} data-testid="import-delete-absents" />
                    <span className="text-sm text-red-300">Supprimer définitivement ces {recap.absents.length} salarié(s) du site</span>
                  </label>
                </div>
              )}
              {recap.nouveaux.length === 0 && recap.modifies.length === 0 && recap.absents.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4 flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Le fichier est identique à la liste actuelle — aucun changement.</p>
              )}
            </div>

            <DialogFooter>
              <Label className="text-xs text-muted-foreground mr-auto">{recap.total_fichier} ligne(s) détectée(s) dans le fichier</Label>
              <Button variant="outline" onClick={reset} data-testid="import-back-button">Choisir un autre fichier</Button>
              <Button onClick={apply} disabled={applying || (recap.nouveaux.length === 0 && recap.modifies.length === 0 && !deleteAbsent)} data-testid="import-apply-button">
                {applying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Application...</> : "Valider et appliquer"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
